import { SetupAnalysisResult } from "@/types/agent/setup";

export const getFieldSuggestions = (field: string, analysis: SetupAnalysisResult): string => {
  switch (field) {
    case "firstMessage": {
      const name = analysis.genieName || "Your Genie";
      const useCase = analysis.useCase || "assist you";
      return `Hi! I'm ${name}. I'm here to help ${useCase}. How can I assist you today?`;
    }

    case "goal": {
      const categoryGoals: Record<string, string> = {
        sales: "Collect lead name, email, company name, company size, budget range, and timeline for decision",
        support:
          "Understand customer issue, gather account details, troubleshoot problem, and route to appropriate team if needed",
        scheduling: "Gather contact information, preferred date/time, meeting type, and any special requirements",
        education:
          "Understand learning goals, current knowledge level, preferred learning style, and areas of interest",
        healthcare: "Collect patient symptoms, medical history, current medications, and reason for consultation",
        finance: "Gather financial goals, current situation, risk tolerance, and investment timeline",
        ecommerce: "Understand product preferences, budget, intended use, and purchase timeline",
        hr: "Collect candidate experience, qualifications, availability, and salary expectations",
        legal: "Understand legal issue, gather relevant details, timeline, and desired outcome",
        realestate: "Collect property preferences, budget, location, timeline, and must-have features",
      };

      return (
        categoryGoals[analysis.category || ""] || "Define what information this genie should gather from conversations"
      );
    }

    case "systemPrompt": {
      const name = analysis.genieName || "assistant";
      const category = analysis.category || "general";

      return `You are ${name}, a helpful ${category} assistant. Your role is to:

1. Greet users warmly and professionally
2. Understand their needs through thoughtful questions
3. Provide accurate, helpful information
4. Guide them toward their goals
5. Maintain a ${analysis.personalityTraits?.join(", ") || "professional"} tone

Keep responses concise, clear, and focused on the user's needs.`;
    }

    case "useCase": {
      const categoryUseCases: Record<string, string> = {
        sales: "qualify leads and schedule sales demos",
        support: "resolve customer issues and answer questions",
        scheduling: "book appointments and manage calendars",
        education: "provide learning guidance and answer questions",
        healthcare: "triage patient concerns and schedule appointments",
        finance: "provide financial guidance and collect client information",
        ecommerce: "help customers find products and complete purchases",
        hr: "screen candidates and schedule interviews",
        legal: "provide initial legal guidance and collect case information",
        realestate: "help clients find properties and schedule viewings",
      };

      return categoryUseCases[analysis.category || ""] || "help users achieve their goals";
    }

    default:
      return "";
  }
};

export const getCategorySuggestions = (category: string) => {
  const suggestions: Record<
    string,
    {
      personalityTraits: string[];
      voiceTone: string;
      maxDuration: number;
      tips: string[];
    }
  > = {
    sales: {
      personalityTraits: ["professional", "friendly", "persuasive"],
      voiceTone: "confident and engaging",
      maxDuration: 60 * 30, // 30 minutes,
      tips: [
        "Keep qualification questions concise",
        "Build rapport before asking for commitment",
        "Always provide next steps clearly",
      ],
    },
    support: {
      personalityTraits: ["empathetic", "patient", "helpful"],
      voiceTone: "calm and reassuring",
      maxDuration: 900,
      tips: [
        "Acknowledge the issue before troubleshooting",
        "Use simple, non-technical language",
        "Confirm resolution before ending",
      ],
    },
    scheduling: {
      personalityTraits: ["efficient", "friendly", "organized"],
      voiceTone: "clear and professional",
      maxDuration: 300,
      tips: [
        "Confirm details before booking",
        "Provide confirmation immediately",
        "Offer alternative times if unavailable",
      ],
    },
  };

  return (
    suggestions[category] || {
      personalityTraits: ["professional", "helpful"],
      voiceTone: "clear and friendly",
      maxDuration: 60 * 30, // 30 minutes,
      tips: ["Keep conversations focused", "Be clear and concise"],
    }
  );
};

export const getVoiceSuggestion = (category: string, personalityTraits: string[] = []): string => {
  // Map categories and traits to voice characteristics
  if (category === "sales" || personalityTraits.includes("energetic")) {
    return "Choose an energetic, confident voice";
  }

  if (category === "support" || personalityTraits.includes("empathetic")) {
    return "Choose a calm, reassuring voice";
  }

  if (category === "education" || personalityTraits.includes("patient")) {
    return "Choose a clear, articulate voice";
  }

  if (personalityTraits.includes("professional")) {
    return "Choose a professional, neutral voice";
  }

  return "Choose a voice that matches your genie's personality";
};
